import { useQuery } from "@tanstack/react-query";
import type { ChecklistTemplate, RenovationService } from "@/types/components";

const apiUrl = import.meta.env.VITE_CLOUDFLARE_API_URL || "http://localhost:8788";

interface UseTemplateBuilderDataProps {
  templateId?: number | null;
}

export function useTemplateBuilderData({ templateId }: UseTemplateBuilderDataProps) {
  // Fetch template data if editing
  const { data: templateData, isLoading: isLoadingTemplate } = useQuery({
    queryKey: ["template", templateId],
    queryFn: async () => {
      if (!templateId) return null;
      const response = await fetch(`${apiUrl}/api/v1/templates/${templateId}`);
      if (!response.ok) throw new Error("Failed to fetch template");
      const result = await response.json();
      return result.data as ChecklistTemplate;
    },
    enabled: !!templateId,
  });

  // Fetch services
  const { data: services = [], isLoading: isLoadingServices } = useQuery<RenovationService[]>({
    queryKey: ["services"],
    queryFn: async () => {
      const response = await fetch(`${apiUrl}/api/v1/checklists/services`);
      if (!response.ok) throw new Error("Failed to fetch services");
      const result = await response.json();
      return result.data || [];
    },
  });

  const getServiceName = (serviceId: number): string => {
    return services.find((s) => s.id === serviceId)?.name || "Unknown Service";
  };

  return {
    templateData,
    services,
    isLoading: isLoadingTemplate || isLoadingServices,
    getServiceName,
  };
}
